<script lang="ts">
  import { onMount, tick } from "svelte";
  import { fade, slide } from "svelte/transition";

  export let hideAboveRefinementBar = false;

  type ButtonType = "COLLAPSED" | "EXPANDED";

  let buttonState: ButtonType = "COLLAPSED";
  let latestScrollY = 0;
  let isInitialized = false;
  let refinementBarVisible = !hideAboveRefinementBar;

  const EXPAND_SCROLL_THRESHOLD = 16;

  $: shouldUseTransition = isInitialized;

  function updateButtonState(newState: ButtonType) {
    if (buttonState !== newState) {
      buttonState = newState;
    }
  }

  let scrollFrame: number | undefined;

  const handleScroll = () => {
    const currentScrollY = Math.max(0, window.scrollY);
    const isScrollingDown = currentScrollY > latestScrollY;
    const isScrollingUp = currentScrollY < latestScrollY - EXPAND_SCROLL_THRESHOLD;

    if (isScrollingDown) {
      latestScrollY = currentScrollY;
      updateButtonState("COLLAPSED");
    } else if (isScrollingUp) {
      latestScrollY = currentScrollY;
      updateButtonState("EXPANDED");
    }

    /*                                                                                          */
    if (currentScrollY >= document.documentElement.scrollHeight - (window.innerHeight + 20)) {
      updateButtonState("EXPANDED");
    }
  };

  /*                                                     */
  const scrollListener = () => {
    if (scrollFrame !== undefined) {
      return;
    }
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = undefined;
      handleScroll();
    });
  };

  function expandedButtonSlide(node: Element) {
    return shouldUseTransition ? slide(node, { duration: 200, axis: "x" }) : {};
  }

  function expandedButtonFade(node: Element) {
    return shouldUseTransition ? fade(node, { duration: 400 }) : {};
  }

  onMount(() => {
    window.addEventListener("scroll", scrollListener, { passive: true });
    latestScrollY = window.scrollY;
    if (latestScrollY === 0) {
      buttonState = "EXPANDED";
    }

    let observer: IntersectionObserver | undefined;
    if (hideAboveRefinementBar) {
      const refinementBarElement = document.querySelector(".find_refinementBar");
      if (refinementBarElement) {
        /*                                                                                           */
        if (refinementBarElement.getBoundingClientRect().top <= 0) {
          refinementBarVisible = true;
        }
        observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
              refinementBarVisible = true;
            } else {
              if (entry.boundingClientRect.top > 0) {
                refinementBarVisible = false;
              }
            }
          },
          { threshold: 0 },
        );
        observer.observe(refinementBarElement);
      }
    }

    tick().then(() => {
      isInitialized = true;
    });
    return () => {
      window.removeEventListener("scroll", scrollListener);
      if (scrollFrame !== undefined) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = undefined;
      }
      observer?.disconnect();
    };
  });
</script>

<div id="harmonized-filter-btn" class="floating-action-button-container-filter" class:hidden={!refinementBarVisible}>
  <button
    id="find_quickFilterButton"
    type="button"
    aria-label="Filter"
    class="floating-action-button js_openFilterSheet js_san_quickFilter_button"
    data-ts-labels={JSON.stringify({ san_Foldout: "filter_bottom_all" })}
  >
    <div class="floating-action-button-icon-wrapper">
      <oc-icon-v1 size="100" color="white" type="filter"></oc-icon-v1>
    </div>
    {#if buttonState === "EXPANDED"}
      <div transition:expandedButtonSlide class="expanded-text">
        <span transition:expandedButtonFade>Filter</span>
      </div>
    {/if}
  </button>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  .hidden {
    visibility: hidden;
  }
  .floating-action-button-container-filter {
    position: fixed;
    bottom: 20px;
    right: 16px;
    z-index: 101;
    pointer-events: all;
    max-width: 1152px;
    display: flex;
    justify-content: flex-end;
  }
  .floating-action-button {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0 tokens.$oc-semantic-spacing-75;
    border: none;
    border-radius: tokens.$oc-semantic-border-radius-100;
    background-color: tokens.$oc-semantic-color-primary-background;
    filter: tokens.$oc-semantic-shadow-sticky;
    cursor: pointer;
    transition: width 0.3s ease;
    color: white;
  }
  .floating-action-button-icon-wrapper {
    padding: tokens.$oc-semantic-spacing-75 0;
    line-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .expanded-text {
    width: 71.17px;
    overflow: hidden;
    /*                                                           */
    padding: tokens.$oc-semantic-spacing-50 0 tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-50;

    span {
      color: tokens.$oc-base-color-white;
      font: tokens.$oc-semantic-font-copy-100;
      line-height: tokens.$oc-base-font-line-height-100;
      font-weight: tokens.$oc-base-font-weight-bold;
      white-space: normal;
      overflow-x: hidden;
    }
  }
</style>
